import { useState, useEffect } from "react";
import {
    Server, Video, HardDrive, Info, Clock, CheckCircle2,
    XCircle, Activity, MapPin
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import NvrPortVisualization from "./NvrPortVisualization";
import type { InterfaceEntry } from "./NvrPortVisualization";


interface NvrAssetData {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    job_id: string;
    target_ip: string;
    nvr_brand: string;
    status: string;
    device_name: string;
    system_info: string;
    total_cameras: number;
    active_cameras: number;
    inactive_cameras: number;
    recording: string;
    storage: string;
    device_info: string;
    vendor_note: string | null;
    polled_at: string;
}

interface NvrAssetDetailProps {
    assetId: number;
    clientId: number;
    onInterfacesFetched?: (data: InterfaceEntry[]) => void;
    interfaces?: InterfaceEntry[];
}

// Utility to safely parse JSON strings from the API
const safeParseJSON = (jsonString: string | null) => {
    if (!jsonString) return {};
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return {};
    }
};

export default function NvrAssetDetail({ assetId, clientId, onInterfacesFetched, interfaces = [] }: NvrAssetDetailProps) {

    const [data, setData] = useState<NvrAssetData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchNvrDetails = async () => {
        if (!assetId) return;
        setLoading(true);
        try {
            const params = { query: JSON.stringify({ asset_id: assetId, client_id: clientId }) };
            const response = await baseapi.get("/nvr_asset_detail", { params });

            if (response.data.status && response.data.data && response.data.data.length > 0) {
                setData(response.data.data[0]);
            } else {
                setData(null);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load NVR details");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNvrDetails();
    }, [assetId]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-800/20 border border-gray-700/50 rounded-lg">
                <div className="w-6 h-6 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mb-3"></div>
                <p className="text-[10px] font-black text-gray-200 uppercase tracking-[0.2em]">Loading...</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-800/20 border border-gray-700/50 rounded-lg opacity-60">
                <Server size={24} className="text-gray-200 mb-2" />
                <p className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">No details found</p>
            </div>
        );
    }

    const sysInfo = safeParseJSON(data.system_info);
    const devInfo = safeParseJSON(data.device_info);
    const recInfo = safeParseJSON(data.recording);
    const storageInfo = safeParseJSON(data.storage);

    return (
        <div className="w-full flex flex-col gap-3">

            {/* Network Port Visualization */}
            <div className="bg-gray-800/20 border border-gray-700/50 rounded-xl p-3">
                <NvrPortVisualization
                    assetId={assetId}
                    clientId={clientId}
                    onDataFetched={onInterfacesFetched}
                    initialData={interfaces}
                />
            </div>


            {/* Header / Identity Card */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-3 shadow-sm flex items-center justify-between flex-wrap gap-3">

                <div className="flex items-center gap-3">
                    <div className="bg-indigo-500/10 p-2 rounded-md border border-indigo-500/20">
                        <Server size={20} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-white tracking-wide leading-tight">{data.device_name || "Unknown NVR"}</h2>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-200">
                            <span className="flex items-center gap-1"><MapPin size={10} /> {data.target_ip}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                            <span className="text-indigo-300">{data.nvr_brand}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-600"></span>
                            <span>ID: {data.asset_id}</span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                    {data.status === 'success' ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[9px] font-black uppercase tracking-widest rounded">
                            <CheckCircle2 size={10} /> Online
                        </span>
                    ) : (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[9px] font-black uppercase tracking-widest rounded">
                            <XCircle size={10} /> Offline
                        </span>
                    )}
                    <span className="text-[9px] text-gray-200 font-semibold">
                        Checked: {new Date(data.polled_at).toLocaleString()}
                    </span>
                </div>
            </div>

            {/* Camera Statistics Row */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2.5 flex items-center gap-3">
                    <div className="bg-blue-500/10 p-2 rounded-md text-blue-400"><Video size={16} /></div>
                    <div>
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">Total Cam</p>
                        <p className="text-lg font-black text-white leading-none">{data.total_cameras}</p>
                    </div>
                </div>
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2.5 flex items-center gap-3">
                    <div className="bg-emerald-500/10 p-2 rounded-md text-emerald-400"><CheckCircle2 size={16} /></div>
                    <div>
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">Active</p>
                        <p className="text-lg font-black text-emerald-400 leading-none">{data.active_cameras}</p>
                    </div>
                </div>
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-2.5 flex items-center gap-3">
                    <div className="bg-rose-500/10 p-2 rounded-md text-rose-400"><XCircle size={16} /></div>
                    <div>
                        <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest leading-none mb-1">Inactive</p>
                        <p className="text-lg font-black text-rose-400 leading-none">{data.inactive_cameras}</p>
                    </div>
                </div>
            </div>

            {/* Detailed Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

                {/* Device & System Info */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-3.5 h-full">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700/50">
                        <Info size={14} className="text-cyan-400" />
                        <h3 className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">Device Details</h3>
                    </div>

                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Type</p>
                                <p className="text-[11px] font-semibold text-gray-300">{devInfo.device_type || "-"}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Serial</p>
                                <p className="text-[11px] font-mono text-gray-300 truncate" title={devInfo.serial_number}>{devInfo.serial_number || "-"}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Firmware</p>
                                <span className="inline-block px-1.5 py-0.5 bg-gray-900 border border-gray-700 rounded text-[9px] font-mono text-cyan-300">
                                    v{devInfo.firmware_version || "Unknown"}
                                </span>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Uptime</p>
                                <p className="text-[11px] font-semibold text-gray-300 flex items-center gap-1">
                                    <Activity size={10} className="text-emerald-400" /> {sysInfo.uptime || "-"}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-1">
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Location</p>
                                <p className="text-[11px] font-semibold text-gray-300 truncate" title={sysInfo.location}>{sysInfo.location || "-"}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Contact</p>
                                <p className="text-[11px] font-semibold text-gray-300 truncate" title={sysInfo.contact}>{sysInfo.contact || "-"}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Storage & Recording Info */}
                <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-3.5 h-full">
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-700/50">
                        <HardDrive size={14} className="text-purple-400" />
                        <h3 className="text-[10px] font-bold text-gray-200 uppercase tracking-widest">Storage</h3>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-2.5 bg-gray-900/50 rounded-md border border-gray-800">
                            <div>
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">State</p>
                                <p className="text-[11px] font-bold text-gray-200 capitalize flex items-center gap-1.5">
                                    {recInfo.state === 'stopped' ? (
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                                    ) : recInfo.state ? (
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                    ) : null}
                                    {recInfo.state || "Unknown"}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[9px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Free Space</p>
                                <p className="text-sm font-black text-purple-400 font-mono leading-none">
                                    {storageInfo.free_disk_space || recInfo.free_disk_space || "-"}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-gray-900/30 p-2 rounded-md border border-gray-800">
                                <p className="text-[8px] font-bold text-gray-200 uppercase tracking-widest mb-1 flex items-center gap-1"><Clock size={8} /> Start</p>
                                <p className="text-[10px] font-mono text-gray-300">{recInfo.start_time || "-"}</p>
                            </div>
                            <div className="bg-gray-900/30 p-2 rounded-md border border-gray-800">
                                <p className="text-[8px] font-bold text-gray-200 uppercase tracking-widest mb-1 flex items-center gap-1"><Clock size={8} /> End</p>
                                <p className="text-[10px] font-mono text-gray-300">{recInfo.end_time || "-"}</p>
                            </div>
                        </div>

                        <div className="mt-1 pt-2 border-t border-gray-700/50">
                            <p className="text-[8px] font-bold text-gray-200 uppercase tracking-widest mb-0.5">Job ID</p>
                            <p className="text-[9px] font-mono text-gray-200 truncate">{data.job_id}</p>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}